import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { SessionUser } from '@billflow/shared';

export const AUTH_QUERY_KEY = ['auth', 'me'] as const;

interface MeResponse {
  data: { user: SessionUser };
}

export function useAuth() {
  const { data, isLoading } = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: async () => {
      try {
        // redirectOn401: false — a 401 here just means "not logged in"; the catch
        // below returns null. (Redirecting would loop the login page forever.)
        const res = await apiFetch<MeResponse>('/api/auth/me', { redirectOn401: false });
        return res.data.user;
      } catch {
        return null; // 401 or network error → treat as not logged in
      }
    },
    staleTime: Infinity,
    retry: false,
  });

  return {
    user: data ?? null,
    isLoading,
    isAuthenticated: data != null,
  };
}

export function useInvalidateAuth() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
}
